import React, { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, AlertTriangle, BarChart2, RefreshCw, Info } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

function MarginBar({ pct }) {
  const clamped = Math.max(-100, Math.min(100, pct));
  const width = Math.abs(clamped);
  const color = pct >= 40 ? "#059669" : pct >= 20 ? "#d97706" : pct >= 0 ? "#ea580c" : "#dc2626";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${width}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-bold" style={{ color }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

const PERIOD_LABELS = {
  ultimo_lote: "Ultimo Lote",
  "3m": "3 Meses",
  "6m": "6 Meses",
  "12m": "Anual",
};

const COST_SOURCE_LABELS = { lotes: "Lotes", compras: "Compras", sin_datos: "Sin datos" };

export default function Rentabilidad() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("3m");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/profitability-v2");
      setData(r.data);
    } catch { toast.error("Error al cargar datos de rentabilidad"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Filter products that have data for the selected period
  const withData = data.filter(d => d.periods[period] && d.periods[period].avg_cost > 0);
  const noData = data.filter(d => !d.periods[period] || d.periods[period].avg_cost === 0);

  const chartData = withData
    .filter(d => d.periods[period]?.avg_price > 0)
    .sort((a, b) => (a.periods[period]?.margin_pct || 0) - (b.periods[period]?.margin_pct || 0))
    .slice(-15) // top 15 for chart readability
    .map(d => {
      const p = d.periods[period];
      return {
        name: d.product_name.length > 18 ? d.product_name.slice(0, 16) + "..." : d.product_name,
        "Costo Prom.": p.avg_cost,
        "Precio Prom.": p.avg_price,
        margin_pct: p.margin_pct,
      };
    });

  const avgMargin = withData.length > 0
    ? (withData.reduce((s, d) => s + (d.periods[period]?.margin_pct || 0), 0) / withData.length).toFixed(1)
    : "0.0";
  const sorted = [...withData].sort((a, b) => (b.periods[period]?.margin_pct || 0) - (a.periods[period]?.margin_pct || 0));
  const bestProduct = sorted[0] || null;
  const worstProduct = sorted[sorted.length - 1] || null;
  const fromBatches = withData.filter(d => d.periods[period]?.cost_source === "lotes").length;
  const fromPurchases = withData.filter(d => d.periods[period]?.cost_source === "compras").length;

  return (
    <div className="space-y-5" data-testid="rentabilidad-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Rentabilidad</h1>
          <p className="text-slate-500 text-sm mt-0.5">Basada en precios reales de venta y costos historicos</p>
        </div>
        <button onClick={load} disabled={loading} data-testid="refresh-btn"
          className="flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>

      {/* Period Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="period-tabs">
        {Object.entries(PERIOD_LABELS).map(([key, label]) => (
          <button key={key} onClick={() => setPeriod(key)} data-testid={`period-${key}`}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              period === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5 flex items-start gap-2">
        <Info size={14} className="text-blue-500 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700">
          <strong>Precio Prom.</strong> = promedio real de tus ventas (no precio de lista).{" "}
          <strong>Costo Prom.</strong> = promedio de {period === "ultimo_lote" ? "el ultimo lote producido" : `los ultimos ${PERIOD_LABELS[period].toLowerCase()}`}
          {" "}(lotes de produccion o compras).
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="kpi-avg-margin">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Margen Promedio</div>
          <div className={`text-2xl font-bold mt-1 ${Number(avgMargin) >= 20 ? "text-emerald-600" : "text-amber-600"}`}>{avgMargin}%</div>
          <div className="text-[10px] text-slate-400">{PERIOD_LABELS[period]}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="kpi-best">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Mas Rentable</div>
          <div className="text-sm font-bold text-emerald-600 mt-1 truncate">{bestProduct?.product_name || "-"}</div>
          <div className="text-xs text-slate-500">{bestProduct ? `${bestProduct.periods[period]?.margin_pct?.toFixed(1)}%` : ""}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="kpi-worst">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Menos Rentable</div>
          <div className="text-sm font-bold text-red-600 mt-1 truncate">{worstProduct?.product_name || "-"}</div>
          <div className="text-xs text-slate-500">{worstProduct ? `${worstProduct.periods[period]?.margin_pct?.toFixed(1)}%` : ""}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="kpi-sources">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Fuentes de Costo</div>
          <div className="flex gap-3 mt-1.5">
            <div>
              <div className="text-lg font-bold text-brand-600">{fromBatches}</div>
              <div className="text-[10px] text-slate-400">Lotes</div>
            </div>
            <div>
              <div className="text-lg font-bold text-teal-600">{fromPurchases}</div>
              <div className="text-[10px] text-slate-400">Compras</div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="profitability-chart">
          <div className="flex items-center gap-2 mb-3">
            <BarChart2 size={16} className="text-slate-600" />
            <span className="text-sm font-bold text-slate-700">Costo Promedio vs Precio Promedio de Venta</span>
            <span className="text-xs text-slate-400 ml-1">({PERIOD_LABELS[period]})</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} height={80} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(value, name) => [`$${Number(value).toFixed(2)}`, name]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Costo Prom." fill="#f59e0b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Precio Prom." radius={[3, 3, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.margin_pct >= 20 ? "#059669" : entry.margin_pct >= 0 ? "#d97706" : "#dc2626"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="profitability-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Producto", "Precio Prom. Venta", "Costo Prom.", "Fuente", "Margen", "Datos"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">Cargando...</td></tr>
            ) : withData.length === 0 && noData.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">Sin productos para analizar</td></tr>
            ) : (
              <>
                {[...withData].sort((a, b) => (b.periods[period]?.margin_pct || 0) - (a.periods[period]?.margin_pct || 0)).map(d => {
                  const p = d.periods[period] || {};
                  return (
                    <tr key={d.product_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`profit-row-${d.product_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{d.product_name}</div>
                        <div className="text-[10px] text-slate-400">
                          Lista: {formatMXN(d.list_price)}/{d.sale_unit}
                          {period === "ultimo_lote" && p.batch_number && (
                            <span className="ml-2 text-brand-500">Lote: {p.batch_number} ({p.batch_date})</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">{formatMXN(p.avg_price)}</span>
                        <span className="text-[10px] text-slate-400">/{d.sale_unit}</span>
                        {p.avg_price !== d.list_price && d.list_price > 0 && (
                          <div className="text-[10px] text-slate-400">
                            {p.avg_price > d.list_price ? (
                              <span className="text-emerald-500">+{((p.avg_price - d.list_price) / d.list_price * 100).toFixed(1)}% vs lista</span>
                            ) : (
                              <span className="text-amber-500">{((p.avg_price - d.list_price) / d.list_price * 100).toFixed(1)}% vs lista</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-700">{formatMXN(p.avg_cost)}</span>
                        <span className="text-[10px] text-slate-400">/{d.sale_unit}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          p.cost_source === "lotes" ? "bg-brand-50 text-brand-700" : "bg-teal-50 text-teal-700"
                        }`}>
                          {COST_SOURCE_LABELS[p.cost_source] || p.cost_source}
                        </span>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {p.batch_count > 0 && `${p.batch_count} lotes`}
                          {p.batch_count > 0 && p.purchase_count > 0 && " + "}
                          {p.purchase_count > 0 && `${p.purchase_count} compras`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <MarginBar pct={p.margin_pct} />
                        <div className="text-[10px] text-slate-500 mt-0.5">{formatMXN(p.margin)}/{d.sale_unit}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs font-semibold text-brand-600">{p.data_points || 0}</span>
                      </td>
                    </tr>
                  );
                })}
                {noData.length > 0 && (
                  <>
                    <tr><td colSpan={6} className="bg-amber-50 px-4 py-2">
                      <div className="flex items-center gap-1.5 text-xs text-amber-700 font-medium">
                        <AlertTriangle size={12} /> {noData.length} producto{noData.length !== 1 ? "s" : ""} sin datos de costo en este periodo
                      </div>
                    </td></tr>
                    {noData.map(d => (
                      <tr key={d.product_id} className="border-b border-slate-100 bg-amber-50/30">
                        <td className="px-4 py-2.5"><span className="text-slate-600">{d.product_name}</span></td>
                        <td className="px-4 py-2.5 text-slate-600">{formatMXN(d.list_price)}/{d.sale_unit}</td>
                        <td className="px-4 py-2.5 text-slate-400" colSpan={4}>Sin lotes ni compras registradas en {PERIOD_LABELS[period].toLowerCase()}</td>
                      </tr>
                    ))}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Comparison across all periods */}
      {withData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="comparison-table">
          <div className="px-4 py-3 border-b border-slate-200">
            <span className="text-sm font-bold text-slate-700">Comparativa por Periodo</span>
            <span className="text-xs text-slate-400 ml-2">Margen de cada producto en todos los periodos</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Producto</th>
                {Object.entries(PERIOD_LABELS).map(([k, v]) => (
                  <th key={k} className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase">{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...withData].sort((a, b) => (b.periods["3m"]?.margin_pct || 0) - (a.periods["3m"]?.margin_pct || 0)).map(d => (
                <tr key={d.product_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-900 text-xs">{d.product_name}</td>
                  {Object.keys(PERIOD_LABELS).map(pk => {
                    const pd = d.periods[pk];
                    if (!pd || pd.avg_cost === 0) return <td key={pk} className="text-center px-3 py-2.5 text-[10px] text-slate-300">--</td>;
                    const color = pd.margin_pct >= 40 ? "text-emerald-600" : pd.margin_pct >= 20 ? "text-amber-600" : pd.margin_pct >= 0 ? "text-orange-600" : "text-red-600";
                    return (
                      <td key={pk} className="text-center px-3 py-2.5">
                        <span className={`text-xs font-bold ${color}`}>{pd.margin_pct.toFixed(1)}%</span>
                        <div className="text-[10px] text-slate-400">{formatMXN(pd.avg_cost)}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
